/**
 * Hook for managing story selection state.
 *
 * Replaces compose-app `publicationViewState.selectedStories` + `selectAllStories`.
 *
 * Uses Immutable.Map from compose-app (not native Map) for compatibility with
 * existing codebase patterns.
 *
 * Usage:
 * ```tsx
 * const selection = useStoriesSelection();
 *
 * // Check if story selected
 * selection.isSelected(storyId)
 *
 * // Toggle single story
 * selection.toggle(storyId)
 *
 * // Bulk operations
 * selection.selectMany(['id1', 'id2', 'id3'])
 * selection.clearSelection()
 * selection.toggleSelectAll()
 *
 * // Get selection data
 * const ids = selection.getSelectedIds()
 * const count = selection.getSelectedCount()
 * ```
 */

import { useState, useCallback } from 'react';
import { Map } from '@opengov/compose-app/immutable';

export type UseStoriesSelection = {
  selectedStories: Map<string, boolean>;
  selectAll: boolean;
  bulkChangeStatusJobId: string | null;
  isSelected: (storyId: string) => boolean;
  toggle: (storyId: string) => void;
  selectMany: (storyIds: string[]) => void;
  deselectMany: (storyIds: string[]) => void;
  clearSelection: () => void;
  toggleSelectAll: () => void;
  setSelectAll: (value: boolean) => void;
  getSelectedIds: () => string[];
  getSelectedCount: () => number;
  setBulkChangeStatusJobId: (jobId: string | null) => void;
};

/**
 * Hook for managing story selection state (checkboxes, select-all, bulk operations).
 *
 * Uses Immutable.Map from compose-app for compatibility. Methods return new Map
 * instances (immutable pattern).
 */
export function useStoriesSelection(): UseStoriesSelection {
  // Use Immutable.Map from compose-app, not native Map
  const [selectedStories, setSelectedStories] = useState<Map<string, boolean>>(Map([]));
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [bulkChangeStatusJobId, setBulkChangeStatusJobId] = useState<string | null>(null);

  const isSelected = useCallback(
    (storyId: string): boolean => {
      return selectedStories.get(storyId) === true;
    },
    [selectedStories]
  );

  const toggle = useCallback((storyId: string) => {
    setSelectedStories((prev) => {
      const currentValue = prev.get(storyId);
      // Immutable.Map.set() returns new Map
      return currentValue ? prev.delete(storyId) : prev.set(storyId, true);
    });
  }, []);

  const selectMany = useCallback((storyIds: string[]) => {
    setSelectedStories((prev) => {
      let next = prev;
      storyIds.forEach((id) => {
        next = next.set(id, true);
      });
      return next;
    });
  }, []);

  const deselectMany = useCallback((storyIds: string[]) => {
    setSelectedStories((prev) => {
      let next = prev;
      storyIds.forEach((id) => {
        next = next.delete(id);
      });
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedStories(Map([]));
    setSelectAll(false);
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectAll((prev) => !prev);
    if (selectAll) {
      // If currently all selected, clear
      setSelectedStories(Map([]));
    }
  }, [selectAll]);

  const getSelectedIds = useCallback((): string[] => {
    return selectedStories
      .filter((value) => value === true)
      .keySeq()
      .toArray();
  }, [selectedStories]);

  const getSelectedCount = useCallback((): number => {
    return selectedStories.filter((value) => value === true).size;
  }, [selectedStories]);

  return {
    selectedStories,
    selectAll,
    bulkChangeStatusJobId,
    isSelected,
    toggle,
    selectMany,
    deselectMany,
    clearSelection,
    toggleSelectAll,
    setSelectAll,
    getSelectedIds,
    getSelectedCount,
    setBulkChangeStatusJobId
  };
}
